import { useEffect, useState } from "react";
// eslint-disable-next-line no-restricted-imports -- TODO: update to `next/navigation` before using this component in App Router
import { useRouter } from "next/router";

import getSearchResults from "@/api/requests/getSearchResults";
import getShopResults from "@/api/requests/getShopResults";
import { Brand } from "@/custom-types/Brand";
import { Dispensary } from "@/custom-types/Dispensary";
import { Query } from "@/custom-types/Query";
import { MappedFilterOption } from "@/custom-types/Shop";
import { Strain } from "@/custom-types/Strain";
import { useSelector } from "@/redux/hooks";
import { getCoordinates } from "@/redux/selectors/location";
import generateShopQuery from "@/utils/generateShopQuery";

import useDomainCountryCode from "./useDomainCountryCode";

export const MAX_DISTANCE = 30;

export const isCategoryHub = (query: Query) => {
  const { product_category, q, shopCategory, strain_category, strain_name } =
    query;

  return (
    !!product_category &&
    !q &&
    !shopCategory &&
    !strain_category &&
    !strain_name
  );
};

const useShop = () => {
  const {
    push,
    query: {
      deal_active,
      product_category,
      q,
      shopCategory,
      shopName,
      showSimilar,
      strain_category,
      strain_name,
    },
  } = useRouter();

  const countryCode = useDomainCountryCode();
  const coordinates = useSelector(getCoordinates);

  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState<Brand | Strain | undefined>();
  const [dispensaries, setDispensaries] = useState<Dispensary[]>([]);
  const [filters, setFilters] = useState<MappedFilterOption[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  useEffect(() => {
    const getResults = async () => {
      setLoading(true);

      const selectionQuery = generateShopQuery({
        shopCategory,
        shopName,
        type: shopCategory,
      });

      const shopResultsQuery = generateShopQuery({
        deal_active,
        medical: "false",
        product_category,
        q,
        shopCategory,
        shopName,
        strain_category,
        strain_name,
        type: "menu_item",
      });

      const isSelection =
        (shopCategory === "brand" || shopCategory === "strain") && !!shopName;
      const [selectionResults, shopResults] = await Promise.all([
        isSelection
          ? getSearchResults(
              selectionQuery,
              coordinates,
              MAX_DISTANCE,
              countryCode,
              1,
              0,
              "",
              true,
            )
          : undefined,
        getShopResults(
          shopResultsQuery,
          coordinates,
          MAX_DISTANCE,
          countryCode,
          shopCategory === "strain" &&
            (showSimilar === "on" || showSimilar === "menu_items_only"),
        ),
      ]);

      const selectionResult =
        selectionResults?.[shopCategory as "brand" | "strain"]?.[0];

      if (
        shopCategory === "brand" &&
        selectionResult?.slug &&
        shopResults.totalCount === 0
      ) {
        push(`/brands/${selectionResult.slug}`);
      } else {
        setSelection(selectionResult);
        setDispensaries(shopResults.dispensary);
        setFilters(shopResults.availableFilters);
        setTotalCount(shopResults.totalCount || 0);

        setLoading(false);
      }
    };

    getResults();
  }, [
    deal_active,
    product_category,
    q,
    shopCategory,
    shopName,
    showSimilar,
    strain_category,
    strain_name,
    JSON.stringify(coordinates),
  ]);

  return {
    dispensaries,
    filters,
    loading,
    selection,
    showEmptyState: !totalCount && !loading,
    totalCount,
  };
};

export default useShop;
